import { ReactNode, useState } from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, ChevronDown, ClipboardList, FileText, History, Loader2, LogOut, Plug, Shield, Store, Users } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import {
  addAdminBusinessMember,
  adminStatus,
  approveBusinessRegistration,
  clearAdminSession,
  deleteAdminBusiness,
  deleteProductCategoryHint,
  deleteAdminUser,
  disableAdminIntegration,
  fetchAdminAudit,
  fetchAdminBusinesses,
  fetchAdminBusinessRegistrations,
  fetchAdminIntegrations,
  fetchAdminInvoices,
  fetchAdminIssues,
  fetchAdminOverviewFiltered,
  fetchAdminSuppliers,
  fetchAdminUsers,
  reinstateAdminUser,
  reinstateAdminBusiness,
  rejectBusinessRegistration,
  removeAdminBusinessMember,
  revokeAdminSession,
  startAdminImpersonation,
  suspendAdminBusiness,
  suspendAdminUser,
  updateAdminBusinessMemberRole,
  updateAdminSupplierCategory,
  upsertProductCategoryHint,
} from "../../services/adminService";
import { setAdminImpersonation } from "../../lib/adminImpersonation";

import { ALL_CATEGORIES } from "../../data/categories";
const PRODUCT_CATEGORIES = ALL_CATEGORIES;

const NAVY = "#2a2420";
const ORANGE = "#607840";

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function AdminShell() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();
  const statusQuery = useQuery({ queryKey: ["admin-status"], queryFn: adminStatus, retry: false });

  async function leaveAdmin() {
    try { await revokeAdminSession(); } catch { clearAdminSession(); }
    navigate("/businesses", { replace: true });
  }

  async function logout() {
    clearAdminSession();
    await signOut();
    navigate("/login", { replace: true });
  }

  if (statusQuery.isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" style={{ color: ORANGE }} /></div>;
  }
  if (!statusQuery.data?.isPlatformAdmin) return <Navigate to="/businesses" replace />;
  if (!statusQuery.data?.verified) return <Navigate to="/admin/verify" replace />;

  const nav = [
    { to: "/admin", label: "Dashboard", icon: Shield, end: true },
    { to: "/admin/registrations", label: "Registrations", icon: ClipboardList },
    { to: "/admin/suppliers", label: "Supplier Database", icon: Store },
    { to: "/admin/integrations", label: "Integrations", icon: Plug },
    { to: "/admin/invoices", label: "Invoices / Issues", icon: FileText },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/businesses", label: "Businesses", icon: Building2 },
    { to: "/admin/audit", label: "Audit Log", icon: History },
  ];

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: "#fefcf8" }}>
      <aside className="w-72 flex-shrink-0 flex flex-col" style={{ backgroundColor: "#17212b" }}>
        <div className="px-5 py-5" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <p className="text-sm font-extrabold text-white">Platform Admin</p>
          <p className="text-[11px] mt-1 truncate" style={{ color: "rgba(255,255,255,0.45)" }}>{user?.email}</p>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold"
              style={({ isActive }) => ({
                color: isActive ? "#fff" : "rgba(255,255,255,0.65)",
                backgroundColor: isActive ? ORANGE : "transparent",
              })}
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 space-y-2" style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button onClick={leaveAdmin} className="w-full rounded-lg px-3 py-2 text-left text-xs font-bold" style={{ color: "#fff", backgroundColor: "rgba(255,255,255,0.08)" }}>Leave admin</button>
          <button onClick={logout} className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-xs font-bold" style={{ color: "rgba(255,255,255,0.55)" }}><LogOut size={13} /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-extrabold" style={{ color: NAVY }}>{title}</h1>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Card({ children }: { children: ReactNode }) {
  return <div className="rounded-lg bg-white p-5" style={{ border: `1px solid ${NAVY}12`, boxShadow: "0 2px 10px rgba(80,55,30,0.06)" }}>{children}</div>;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: NAVY, opacity: 0.48 }}>{label}</span>
      {children}
    </label>
  );
}

function inputStyle() {
  return { border: `1px solid ${NAVY}22`, color: NAVY, backgroundColor: "#fff" };
}

function DataTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-hidden rounded-lg bg-white" style={{ border: `1px solid ${NAVY}12` }}>
      <table className="w-full text-sm">
        <thead style={{ backgroundColor: `${NAVY}06` }}>
          <tr>{columns.map((col) => <th key={col} className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest" style={{ color: NAVY, opacity: 0.5 }}>{col}</th>)}</tr>
        </thead>
        <tbody>{rows.map((row, i) => <tr key={i} style={{ borderTop: `1px solid ${NAVY}0d` }}>{row.map((cell, j) => <td key={j} className="px-4 py-3 align-top" style={{ color: NAVY }}>{cell}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function AdminDashboard() {
  const [businessId, setBusinessId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-overview", businessId, startDate, endDate],
    queryFn: () => fetchAdminOverviewFiltered({ businessId, startDate, endDate }),
  });
  const businesses = data?.filters?.businesses || [];

  if (error) return <Page title="Admin Dashboard"><p className="text-red-700">{(error as Error).message}</p></Page>;
  return (
    <Page title="Admin Dashboard">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <Field label="Business">
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
            <option value="">All businesses</option>
            {businesses.map((business: any) => <option key={business.id} value={business.id}>{business.name}</option>)}
          </select>
        </Field>
        <Field label="From"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
        <Field label="To"><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
        <div className="flex items-end"><button onClick={() => { setBusinessId(""); setStartDate(""); setEndDate(""); }} className="w-full rounded-lg px-3 py-2 text-sm font-bold" style={{ color: NAVY, backgroundColor: `${NAVY}0a` }}>Reset</button></div>
      </div>

      {isLoading ? <Loader2 className="animate-spin" /> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            {[["Businesses", data?.totals?.businesses], ["Users", data?.totals?.users], ["Suppliers", data?.totals?.suppliers]].map(([label, value]) => (
              <Card key={label as string}><p className="text-xs font-bold" style={{ color: NAVY, opacity: 0.45 }}>{label}</p><p className="text-3xl font-extrabold mt-2" style={{ color: NAVY }}>{String(value ?? 0)}</p></Card>
            ))}
          </div>
          <DataTable columns={["Business", "Users in filter", "Suppliers in filter"]} rows={(data?.businesses || []).map((business: any) => [business.name, business.userCount, business.supplierCount])} />
        </>
      )}
    </Page>
  );
}

export function AdminSuppliers() {
  const queryClient = useQueryClient();
  const [businessId, setBusinessId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [openSupplierId, setOpenSupplierId] = useState("");
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingSupplierCatId, setEditingSupplierCatId] = useState<string | null>(null);

  const queryKey = ["admin-suppliers", businessId, supplierId] as const;

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => fetchAdminSuppliers({ businessId, supplierId }),
    staleTime: 2 * 60 * 1000,
  });

  type HintPayload = { productCode?: string; description: string; suggestedCategory: string };
  type HintResult = { id: string; productCode: string | null; descriptionNormalized: string; suggestedCategory: string; updatedAt: string };
  type OptCtx = { previous: unknown };
  type SupplierCatPayload = { sid: string; category: string };
  type SupplierCatResult = { id: string; name: string; category: string; business_id: string };

  // Optimistic hint upsert — updates cache immediately, patches in real hintId on success
  const upsertHint = useMutation<HintResult, Error, HintPayload, OptCtx>({
    mutationFn: upsertProductCategoryHint,
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      const descNorm = vars.description.trim().toLowerCase();
      const codeNorm = (vars.productCode || "").trim().toLowerCase();
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          suppliers: old.suppliers.map((s: any) => ({
            ...s,
            uniqueLineItems: s.uniqueLineItems.map((item: any) => {
              const iCode = (item.productCode || "").trim().toLowerCase();
              const iDesc = item.description.trim().toLowerCase();
              const match = (codeNorm && iCode && iCode === codeNorm) || iDesc === descNorm;
              return match ? { ...item, suggestedCategory: vars.suggestedCategory } : item;
            }),
          })),
        };
      });
      setEditingKey(null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
    onSuccess: (serverData) => {
      // Patch in real hintId so delete works without a full refetch
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        const descNorm = serverData.descriptionNormalized;
        return {
          ...old,
          suppliers: old.suppliers.map((s: any) => ({
            ...s,
            uniqueLineItems: s.uniqueLineItems.map((item: any) =>
              item.description.trim().toLowerCase() === descNorm
                ? { ...item, hintId: serverData.id }
                : item
            ),
          })),
        };
      });
    },
  });

  // Optimistic hint remove
  const removeHint = useMutation<{ success: boolean; id: string }, Error, string, OptCtx>({
    mutationFn: deleteProductCategoryHint,
    onMutate: async (hintId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          suppliers: old.suppliers.map((s: any) => ({
            ...s,
            uniqueLineItems: s.uniqueLineItems.map((item: any) =>
              item.hintId === hintId ? { ...item, suggestedCategory: null, hintId: null } : item
            ),
          })),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
  });

  // Optimistic supplier category update
  const updateSupplierCat = useMutation<SupplierCatResult, Error, SupplierCatPayload, OptCtx>({
    mutationFn: ({ sid, category }) => updateAdminSupplierCategory(sid, category),
    onMutate: async ({ sid, category }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return old;
        return {
          ...old,
          suppliers: old.suppliers.map((s: any) =>
            s.id === sid ? { ...s, category } : s
          ),
        };
      });
      setEditingSupplierCatId(null);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
    },
  });

  const businesses = data?.filters?.businesses || [];
  const supplierOptions = data?.filters?.suppliers || [];
  const suppliers = data?.suppliers || [];

  function itemEditKey(suppId: string, item: any) {
    return `${suppId}|${item.description}|${item.productCode}`;
  }

  function CategoryCell({ supplier, item }: { supplier: any; item: any }) {
    const key = itemEditKey(supplier.id, item);
    const isEditing = editingKey === key;
    const hasHint = !!item.suggestedCategory;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1.5">
          <select
            autoFocus
            defaultValue={item.suggestedCategory || item.category || "Other"}
            className="rounded-md px-2 py-1 text-xs outline-none"
            style={{ border: `1px solid ${ORANGE}`, color: NAVY, backgroundColor: "#fff" }}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) return;
              upsertHint.mutate({ productCode: item.productCode || undefined, description: item.description, suggestedCategory: val });
            }}
          >
            <option value="" disabled>Pick category…</option>
            {PRODUCT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <button onClick={() => setEditingKey(null)} className="text-[10px] font-bold px-1.5 py-1 rounded" style={{ color: NAVY, opacity: 0.45, backgroundColor: `${NAVY}0a` }}>✕</button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-1.5 group">
        <button
          onClick={() => setEditingKey(key)}
          title={hasHint ? "Platform hint — click to change" : "No hint set — click to add"}
          className="text-xs rounded px-1.5 py-0.5 text-left"
          style={{ color: hasHint ? "#fff" : NAVY, backgroundColor: hasHint ? ORANGE : `${NAVY}0d`, fontWeight: hasHint ? 700 : 400 }}
        >
          {item.suggestedCategory || item.category || "Other"}
        </button>
        {hasHint && (
          <button
            onClick={() => window.confirm("Remove platform category hint for this product?") && removeHint.mutate(item.hintId)}
            title="Remove hint"
            className="opacity-0 group-hover:opacity-100 text-[9px] px-1 py-0.5 rounded"
            style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}
          >
            ✕
          </button>
        )}
      </div>
    );
  }

  function SupplierCategoryCell({ supplier }: { supplier: any }) {
    const isEditing = editingSupplierCatId === supplier.id;
    if (isEditing) {
      return (
        <div className="flex items-center gap-1.5">
          <select
            autoFocus
            defaultValue={supplier.category || "Other"}
            className="rounded-md px-2 py-1 text-xs outline-none"
            style={{ border: `1px solid ${ORANGE}`, color: NAVY, backgroundColor: "#fff" }}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) return;
              updateSupplierCat.mutate({ sid: supplier.id, category: val });
            }}
          >
            <option value="" disabled>Pick category…</option>
            {PRODUCT_CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
          </select>
          <button onClick={() => setEditingSupplierCatId(null)} className="text-[10px] font-bold px-1.5 py-1 rounded" style={{ color: NAVY, opacity: 0.45, backgroundColor: `${NAVY}0a` }}>✕</button>
        </div>
      );
    }
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setEditingSupplierCatId(supplier.id); }}
        title="Set supplier default category"
        className="text-xs rounded px-1.5 py-0.5"
        style={{ color: NAVY, backgroundColor: `${NAVY}0d` }}
      >
        {supplier.category || "Other"}
      </button>
    );
  }

  return (
    <Page title="Supplier Database">
      <div className="mb-4 rounded-lg px-4 py-3 text-xs" style={{ backgroundColor: `${ORANGE}12`, color: NAVY, border: `1px solid ${ORANGE}30` }}>
        <strong>Category hints:</strong> Click the supplier category (grey pill on card header) to set the default for all its products.
        Click any line item category pill to set a product-specific hint. Orange pills = platform hint active.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <Field label="Business">
          <select value={businessId} onChange={(e) => { setBusinessId(e.target.value); setSupplierId(""); }} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
            <option value="">All businesses</option>
            {businesses.map((business: any) => <option key={business.id} value={business.id}>{business.name}</option>)}
          </select>
        </Field>
        <Field label="Supplier">
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
            <option value="">All suppliers</option>
            {supplierOptions.map((supplier: any) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
          </select>
        </Field>
      </div>
      {upsertHint.isError && (
        <p className="mb-3 rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>
          {(upsertHint.error as Error).message}
        </p>
      )}
      {updateSupplierCat.isError && (
        <p className="mb-3 rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>
          {(updateSupplierCat.error as Error).message}
        </p>
      )}
      {error ? (
        <p className="rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>{(error as Error).message}</p>
      ) : isLoading ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-3">
          {suppliers.length === 0 && <Card><p className="text-sm" style={{ color: NAVY, opacity: 0.55 }}>No suppliers found for the selected filters.</p></Card>}
          {suppliers.map((supplier: any) => {
            const open = openSupplierId === supplier.id;
            const hintCount = (supplier.uniqueLineItems || []).filter((item: any) => item.suggestedCategory).length;
            return (
              <Card key={supplier.id}>
                <div className="flex items-center justify-between gap-3">
                  <button type="button" onClick={() => setOpenSupplierId(open ? "" : supplier.id)} className="flex-1 min-w-0 text-left">
                    <span className="block text-sm font-extrabold" style={{ color: NAVY }}>{supplier.name}</span>
                    <span className="block text-xs mt-1" style={{ color: NAVY, opacity: 0.55 }}>
                      {supplier.businessName} | {supplier.uniqueLineItems.length} unique line items | {supplier.invoiceCount} invoices
                      {hintCount > 0 && (
                        <span className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: `${ORANGE}18`, color: ORANGE }}>
                          {hintCount} product hint{hintCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </span>
                  </button>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <SupplierCategoryCell supplier={supplier} />
                    <button type="button" onClick={() => setOpenSupplierId(open ? "" : supplier.id)}>
                      <ChevronDown size={16} style={{ color: NAVY, transform: open ? "rotate(180deg)" : "none" }} />
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="mt-4">
                    <DataTable
                      columns={["Line item", "Code", "Category / Hint", "Unit", "Seen"]}
                      rows={supplier.uniqueLineItems.map((item: any) => [
                        item.description,
                        item.productCode || "-",
                        <CategoryCell key={itemEditKey(supplier.id, item)} supplier={supplier} item={item} />,
                        item.unit || "-",
                        item.seenCount,
                      ])}
                    />
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Page>
  );
}

export function AdminIntegrations() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-integrations"], queryFn: fetchAdminIntegrations });
  const disable = useMutation({ mutationFn: disableAdminIntegration, onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-integrations"] }) });
  return (
    <Page title="Integrations">
      {isLoading ? <Loader2 className="animate-spin" /> : (
        <div className="space-y-3">
          {(data?.businesses || []).map((business: any) => (
            <Card key={business.id}>
              <p className="text-sm font-extrabold mb-3" style={{ color: NAVY }}>{business.name}</p>
              {business.connections.length === 0 ? <p className="text-sm" style={{ color: NAVY, opacity: 0.48 }}>No storage connection.</p> : (
                <DataTable columns={["Provider", "Status", "Auto export", "Folder", "Last export", "Action"]} rows={business.connections.map((item: any) => [
                  item.provider,
                  item.status,
                  item.auto_export ? "On" : "Off",
                  item.root_folder_name || "-",
                  item.last_export_status || "None",
                  <button onClick={() => window.confirm("Disable this integration?") && disable.mutate(item.id)} className="rounded-md px-2 py-1 text-xs font-bold" style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}>Disable</button>,
                ])} />
              )}
            </Card>
          ))}
        </div>
      )}
    </Page>
  );
}

export function AdminInvoices() {
  const [businessId, setBusinessId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-invoices", businessId, startDate, endDate],
    queryFn: () => fetchAdminInvoices({ businessId, startDate, endDate }),
  });
  const selectedBusiness = data?.selectedBusiness;

  return (
    <Page title="Invoices / Issues">
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
        <Card>
          <p className="text-xs font-bold mb-3" style={{ color: NAVY, opacity: 0.55 }}>Businesses</p>
          <div className="space-y-2">
            {(data?.businesses || []).map((business: any) => (
              <button key={business.id} onClick={() => { setBusinessId(business.id); setStartDate(""); setEndDate(""); }} className="w-full rounded-lg px-3 py-2 text-left" style={{ backgroundColor: businessId === business.id ? `${ORANGE}18` : `${NAVY}08`, color: NAVY }}>
                <span className="block text-sm font-bold">{business.name}</span>
                <span className="block text-xs mt-1" style={{ opacity: 0.55 }}>{business.invoiceCount} invoices | {business.issueCount} issues | latest {business.latestDay || "none"}</span>
              </button>
            ))}
          </div>
        </Card>
        <div>
          {!businessId ? <Card><p className="text-sm" style={{ color: NAVY }}>Choose a business to view invoices for its latest day.</p></Card> : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <Field label="Business"><div className="rounded-lg px-3 py-2 text-sm font-bold" style={{ ...inputStyle(), backgroundColor: `${NAVY}06` }}>{selectedBusiness?.name || "Selected business"}</div></Field>
                <Field label="From"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder={data?.defaultDate || todayIso()} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
                <Field label="To"><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} placeholder={data?.defaultDate || todayIso()} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
              </div>
              {isLoading ? <Loader2 className="animate-spin" /> : <DataTable columns={["Date", "Supplier", "Invoice #", "Total", "Status", "Issues"]} rows={(data?.invoices || []).map((invoice: any) => [invoice.day, invoice.supplier?.name || "-", invoice.invoiceNumber || "Missing", invoice.total || "-", invoice.status, invoice.issueCount])} />}
            </>
          )}
        </div>
      </div>
    </Page>
  );
}

export function AdminUsers() {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [openBusinessId, setOpenBusinessId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [businessSearch, setBusinessSearch] = useState("");
  const [newMemberUserId, setNewMemberUserId] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("staff");
  const [reason] = useState("Suspended by platform admin");
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: fetchAdminUsers });
  const suspend = useMutation({ mutationFn: (userId: string) => suspendAdminUser(userId, reason), onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }) });
  const reinstate = useMutation({ mutationFn: reinstateAdminUser, onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }) });
  const deleteUser = useMutation({
    mutationFn: deleteAdminUser,
    onSuccess: () => {
      setSelectedUser(null);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
  const impersonate = useMutation({
    mutationFn: startAdminImpersonation,
    onSuccess: (session) => {
      setAdminImpersonation(session);
      window.location.assign("/");
    },
  });
  const changeRole = useMutation({
    mutationFn: ({ businessId, userId, role }: { businessId: string; userId: string; role: string }) => updateAdminBusinessMemberRole(businessId, userId, role),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const removeMember = useMutation({
    mutationFn: ({ businessId, userId }: { businessId: string; userId: string }) => removeAdminBusinessMember(businessId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
  });
  const addMember = useMutation({
    mutationFn: ({ businessId }: { businessId: string }) => addAdminBusinessMember(businessId, { userId: newMemberUserId || undefined, email: newMemberEmail || undefined, role: newMemberRole }),
    onSuccess: () => {
      setNewMemberUserId("");
      setNewMemberEmail("");
      setNewMemberRole("staff");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
  });
  const users = data?.users || [];
  const filteredUsers = users.filter((user: any) => {
    const haystack = `${user.name || ""} ${user.email || ""} ${user.phone || ""}`.toLowerCase();
    return haystack.includes(userSearch.toLowerCase());
  });
  const filteredBusinesses = (data?.businesses || []).filter((business: any) => business.name.toLowerCase().includes(businessSearch.toLowerCase()));

  return (
    <Page title="Users">
      {isLoading ? <Loader2 className="animate-spin" /> : (
        <>
          <Card>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <p className="text-xs font-bold" style={{ color: NAVY, opacity: 0.55 }}>All users</p>
              <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search name, email, phone" className="w-full md:w-80 rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} />
            </div>
            <div className="flex flex-wrap gap-4">
              {filteredUsers.map((user: any) => (
                <button key={user.id} onClick={() => setSelectedUser(user)} title={user.email || user.id} className="w-24 flex flex-col items-center gap-2 text-center">
                  <span className="w-12 h-12 rounded-full flex items-center justify-center text-sm font-extrabold text-white" style={{ backgroundColor: user.suspension && !user.suspension.reinstated_at ? "#991b1b" : ORANGE }}>
                    {(user.name || user.email || user.id).slice(0, 2).toUpperCase()}
                  </span>
                  <span className="w-full truncate text-xs font-bold" style={{ color: NAVY }}>{user.name || user.email || "Unnamed user"}</span>
                  <span className="w-full truncate text-[10px]" style={{ color: NAVY, opacity: 0.45 }}>{user.email || ""}</span>
                </button>
              ))}
            </div>
          </Card>

          <div className="mt-5 space-y-3">
            <input value={businessSearch} onChange={(e) => setBusinessSearch(e.target.value)} placeholder="Search business" className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} />
            {filteredBusinesses.map((business: any) => {
              const open = openBusinessId === business.id;
              return (
                <Card key={business.id}>
                  <button type="button" onClick={() => setOpenBusinessId(open ? "" : business.id)} className="w-full flex items-center justify-between text-left">
                    <span>
                      <span className="block text-sm font-extrabold" style={{ color: NAVY }}>{business.name}</span>
                      <span className="block text-xs mt-1" style={{ color: NAVY, opacity: 0.55 }}>{business.users.length} users</span>
                    </span>
                    <ChevronDown size={16} style={{ color: NAVY, transform: open ? "rotate(180deg)" : "none" }} />
                  </button>
                  {open && (
                    <div className="mt-4 space-y-4">
                      <DataTable columns={["Name", "Email", "Phone", "Role", "Action"]} rows={business.users.map((user: any) => [
                        user.name || "Unnamed user",
                        user.email || "-",
                        user.phone || "-",
                        <select value={user.role} onChange={(e) => changeRole.mutate({ businessId: business.id, userId: user.id, role: e.target.value })} className="rounded-md px-2 py-1 text-xs outline-none" style={inputStyle()}>
                          <option value="owner">Owner</option>
                          <option value="staff">Staff</option>
                        </select>,
                        <button onClick={() => window.confirm(`Remove ${user.name || user.email || user.id} from ${business.name}?`) && removeMember.mutate({ businessId: business.id, userId: user.id })} className="rounded-md px-2 py-1 text-xs font-bold" style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}>Remove</button>,
                      ])} />
                      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_120px_120px] gap-2 rounded-lg p-3" style={{ backgroundColor: `${NAVY}06` }}>
                        <select value={newMemberUserId} onChange={(e) => { setNewMemberUserId(e.target.value); setNewMemberEmail(""); }} className="rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
                          <option value="">Choose existing user</option>
                          {users.map((user: any) => <option key={user.id} value={user.id}>{user.name || user.email} {user.email ? `(${user.email})` : ""}</option>)}
                        </select>
                        <input value={newMemberEmail} onChange={(e) => { setNewMemberEmail(e.target.value); setNewMemberUserId(""); }} placeholder="Or enter user email" className="rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} />
                        <select value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)} className="rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
                          <option value="owner">Owner</option>
                          <option value="staff">Staff</option>
                        </select>
                        <button onClick={() => addMember.mutate({ businessId: business.id })} disabled={addMember.isPending || (!newMemberUserId && !newMemberEmail)} className="rounded-lg px-3 py-2 text-sm font-bold text-white disabled:opacity-40" style={{ backgroundColor: ORANGE }}>Add</button>
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}
          </div>

          {selectedUser && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(23,33,43,0.55)" }}>
              <div className="w-full max-w-lg rounded-lg bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-lg font-extrabold" style={{ color: NAVY }}>{selectedUser.name || selectedUser.email || selectedUser.id}</p>
                    <p className="text-xs mt-1" style={{ color: NAVY, opacity: 0.5 }}>User id: {selectedUser.id}</p>
                  </div>
                  <button onClick={() => setSelectedUser(null)} className="rounded-md px-2 py-1 text-xs font-bold" style={{ backgroundColor: `${NAVY}0a`, color: NAVY }}>Close</button>
                </div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" style={{ color: NAVY }}>
                  <Card><p className="text-xs font-bold opacity-50">Email</p><p className="mt-1 break-words">{selectedUser.email || "Not set"}</p></Card>
                  <Card><p className="text-xs font-bold opacity-50">Phone</p><p className="mt-1">{selectedUser.phone || "Not set"}</p></Card>
                  <Card><p className="text-xs font-bold opacity-50">Last sign in</p><p className="mt-1">{selectedUser.lastSignInAt || "Never"}</p></Card>
                  <Card><p className="text-xs font-bold opacity-50">Status</p><p className="mt-1">{selectedUser.suspension && !selectedUser.suspension.reinstated_at ? "Suspended" : "Active"}</p></Card>
                  <Card><p className="text-xs font-bold opacity-50">Created</p><p className="mt-1">{selectedUser.createdAt || "Unknown"}</p></Card>
                  <Card><p className="text-xs font-bold opacity-50">Email confirmed</p><p className="mt-1">{selectedUser.emailConfirmedAt || "Not confirmed"}</p></Card>
                </div>
                <div className="mt-4">
                  <p className="text-xs font-bold mb-2" style={{ color: NAVY, opacity: 0.55 }}>Role summary</p>
                  <DataTable columns={["Business", "Role"]} rows={(selectedUser.businesses || []).map((business: any) => [business.businessName, business.role])} />
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => impersonate.mutate(selectedUser.id)}
                    disabled={impersonate.isPending || (selectedUser.businesses || []).length === 0}
                    className="rounded-lg px-3 py-2 text-xs font-bold text-white disabled:opacity-40"
                    style={{ backgroundColor: ORANGE }}
                  >
                    {impersonate.isPending ? "Opening..." : "View as user"}
                  </button>
                  {selectedUser.suspension && !selectedUser.suspension.reinstated_at
                    ? <button onClick={() => reinstate.mutate(selectedUser.id)} className="rounded-lg px-3 py-2 text-xs font-bold" style={{ backgroundColor: `${NAVY}0a`, color: NAVY }}>Reinstate user</button>
                    : <button onClick={() => window.confirm(`Suspend ${selectedUser.email || selectedUser.id}?`) && suspend.mutate(selectedUser.id)} className="rounded-lg px-3 py-2 text-xs font-bold" style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}>Suspend user</button>}
                  <button
                    onClick={() => window.confirm(`Permanently delete ${selectedUser.email || selectedUser.id}? This cannot be undone.`) && deleteUser.mutate(selectedUser.id)}
                    disabled={deleteUser.isPending || selectedUser.isPlatformAdmin}
                    className="rounded-lg px-3 py-2 text-xs font-bold disabled:opacity-40"
                    style={{ color: "#fefcf8", backgroundColor: "#991b1b" }}
                    title={selectedUser.isPlatformAdmin ? "Platform admins cannot be deleted here." : "Delete user permanently"}
                  >
                    {deleteUser.isPending ? "Deleting..." : "Delete user"}
                  </button>
                </div>
                {impersonate.error && <p className="mt-2 text-xs text-red-700">{(impersonate.error as Error).message}</p>}
                {deleteUser.error && <p className="mt-2 text-xs text-red-700">{(deleteUser.error as Error).message}</p>}
              </div>
            </div>
          )}
        </>
      )}
    </Page>
  );
}

export function AdminBusinesses() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["admin-businesses"], queryFn: fetchAdminBusinesses });

  function refreshAdminBusinessData() {
    queryClient.invalidateQueries({ queryKey: ["admin-businesses"] });
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    queryClient.invalidateQueries({ queryKey: ["admin-invoices"] });
    queryClient.invalidateQueries({ queryKey: ["admin-overview"] });
    queryClient.invalidateQueries({ queryKey: ["admin-audit"] });
  }

  const suspendBusiness = useMutation({
    mutationFn: ({ businessId, reason }: { businessId: string; reason: string }) => suspendAdminBusiness(businessId, reason),
    onSuccess: refreshAdminBusinessData,
  });
  const reinstateBusiness = useMutation({
    mutationFn: reinstateAdminBusiness,
    onSuccess: refreshAdminBusinessData,
  });
  const deleteBusiness = useMutation({
    mutationFn: ({ businessId, confirmName }: { businessId: string; confirmName: string }) => deleteAdminBusiness(businessId, confirmName),
    onSuccess: refreshAdminBusinessData,
  });

  function requestSuspend(business: any) {
    const reason = window.prompt(`Reason for suspending ${business.name}?`, business.suspensionReason || "Suspended by platform admin");
    if (reason === null) return;
    suspendBusiness.mutate({ businessId: business.id, reason });
  }

  function requestDelete(business: any) {
    const confirmName = window.prompt(`This permanently deletes ${business.name} and its business data. Type the business name to continue.`);
    if (confirmName === null) return;
    if (confirmName !== business.name) {
      window.alert("Business name did not match. Nothing was deleted.");
      return;
    }
    deleteBusiness.mutate({ businessId: business.id, confirmName });
  }

  const actionError = suspendBusiness.error || reinstateBusiness.error || deleteBusiness.error;
  const isMutating = suspendBusiness.isPending || reinstateBusiness.isPending || deleteBusiness.isPending;
  const businesses = data || [];

  return (
    <Page title="Businesses">
      {actionError && <p className="mb-3 rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>{(actionError as Error).message}</p>}
      {error ? (
        <p className="rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>{(error as Error).message}</p>
      ) : isLoading ? <Loader2 className="animate-spin" /> : (
        <DataTable
          columns={["Business", "Status", "Members", "Invoices", "Issues", "Integrations", "Reports", "Actions"]}
          rows={businesses.map((business: any) => {
            const suspended = (business.status || "active") === "suspended";
            return [
              <div>
                <p className="font-bold">{business.name}</p>
                <p className="mt-1 text-[10px]" style={{ color: NAVY, opacity: 0.45 }}>{business.industry || "No industry"} | created {business.createdAt ? new Date(business.createdAt).toLocaleDateString() : "unknown"}</p>
              </div>,
              <div>
                <span className="rounded-full px-2 py-1 text-[10px] font-bold" style={{ color: suspended ? "#991b1b" : ORANGE, backgroundColor: suspended ? "rgba(220,38,38,0.08)" : `${ORANGE}18` }}>
                  {suspended ? "Suspended" : "Active"}
                </span>
                {suspended && business.suspensionReason && <p className="mt-2 text-[10px] max-w-xs" style={{ color: NAVY, opacity: 0.55 }}>{business.suspensionReason}</p>}
              </div>,
              business.memberCount,
              business.invoiceCount,
              business.flaggedInvoiceCount,
              <span>{business.integrationCount}{business.failedIntegrationCount > 0 ? ` (${business.failedIntegrationCount} failed)` : ""}</span>,
              business.reportCount,
              <div className="flex flex-wrap gap-2">
                {suspended ? (
                  <button
                    onClick={() => window.confirm(`Reinstate ${business.name}?`) && reinstateBusiness.mutate(business.id)}
                    disabled={isMutating}
                    className="rounded-md px-2 py-1 text-xs font-bold disabled:opacity-40"
                    style={{ color: NAVY, backgroundColor: `${NAVY}0a` }}
                  >
                    Reinstate
                  </button>
                ) : (
                  <button
                    onClick={() => requestSuspend(business)}
                    disabled={isMutating}
                    className="rounded-md px-2 py-1 text-xs font-bold disabled:opacity-40"
                    style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}
                  >
                    Suspend
                  </button>
                )}
                <button
                  onClick={() => requestDelete(business)}
                  disabled={isMutating}
                  className="rounded-md px-2 py-1 text-xs font-bold disabled:opacity-40"
                  style={{ color: "#fefcf8", backgroundColor: "#991b1b" }}
                >
                  Delete
                </button>
              </div>,
            ];
          })}
        />
      )}
    </Page>
  );
}

export function AdminIssues() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-issues"], queryFn: fetchAdminIssues });
  if (isLoading) return <Page title="Issues"><Loader2 className="animate-spin" /></Page>;
  return (
    <Page title="Operational Issues">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><p className="font-bold" style={{ color: NAVY }}>Failed integrations</p><pre className="mt-3 text-xs whitespace-pre-wrap">{JSON.stringify(data?.failedIntegrations || [], null, 2)}</pre></Card>
        <Card><p className="font-bold" style={{ color: NAVY }}>Failed reports</p><pre className="mt-3 text-xs whitespace-pre-wrap">{JSON.stringify(data?.failedReports || [], null, 2)}</pre></Card>
        <Card><p className="font-bold" style={{ color: NAVY }}>Flagged invoices</p><p className="mt-2 text-3xl font-extrabold">{data?.flaggedInvoices?.length || 0}</p></Card>
        <Card><p className="font-bold" style={{ color: NAVY }}>Pending join requests</p><p className="mt-2 text-3xl font-extrabold">{data?.pendingJoinRequests?.length || 0}</p></Card>
      </div>
    </Page>
  );
}

export function AdminAudit() {
  const [businessId, setBusinessId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-audit", businessId, startDate, endDate],
    queryFn: () => fetchAdminAudit({ businessId, startDate, endDate }),
  });
  const businesses = data?.filters?.businesses || [];
  const activities = data?.activities || [];
  return (
    <Page title="Audit Log">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <Field label="Business">
          <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()}>
            <option value="">All businesses</option>
            {businesses.map((business: any) => <option key={business.id} value={business.id}>{business.name}</option>)}
          </select>
        </Field>
        <Field label="From"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
        <Field label="To"><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={inputStyle()} /></Field>
        <div className="flex items-end"><button onClick={() => { setBusinessId(""); setStartDate(""); setEndDate(""); }} className="w-full rounded-lg px-3 py-2 text-sm font-bold" style={{ color: NAVY, backgroundColor: `${NAVY}0a` }}>Reset</button></div>
      </div>
      {isLoading ? <Loader2 className="animate-spin" /> : (
        <DataTable
          columns={["When", "Business", "User", "Action", "Target", "Metadata"]}
          rows={activities.map((a: any) => [
            a.createdAt,
            a.businessName || "Platform",
            <div><p>{a.actorName || "System"}</p>{a.actorEmail && <p className="text-[10px] opacity-50">{a.actorEmail}</p>}</div>,
            a.action,
            a.target || "-",
            <span className="font-mono text-xs break-all">{JSON.stringify(a.metadata || {})}</span>,
          ])}
        />
      )}
    </Page>
  );
}

export function AdminRegistrations() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "rejected">("pending");
  const [actionId, setActionId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(null);
  const [adminNote, setAdminNote] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-registrations", statusFilter],
    queryFn: () => fetchAdminBusinessRegistrations(statusFilter),
  });

  const approve = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => approveBusinessRegistration(id, note || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-registrations"] });
      setActionId(null);
      setAdminNote("");
    },
  });

  const reject = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectBusinessRegistration(id, note || undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-registrations"] });
      setActionId(null);
      setAdminNote("");
    },
  });

  const requests: any[] = data || [];
  const isPending = approve.isPending || reject.isPending;

  function openAction(id: string, type: "approve" | "reject") {
    setActionId(id);
    setActionType(type);
    setAdminNote("");
  }

  function closeAction() {
    setActionId(null);
    setActionType(null);
    setAdminNote("");
  }

  function confirmAction() {
    if (!actionId || !actionType) return;
    if (actionType === "approve") approve.mutate({ id: actionId, note: adminNote });
    else reject.mutate({ id: actionId, note: adminNote });
  }

  const activeRequest = requests.find((r: any) => r.id === actionId);

  return (
    <Page title="Business Registrations">
      {/* Status filter tabs */}
      <div className="flex gap-2 mb-5">
        {(["pending", "approved", "rejected"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className="px-4 py-1.5 rounded-full text-xs font-bold capitalize transition-all"
            style={{
              backgroundColor: statusFilter === s ? ORANGE : `${NAVY}0d`,
              color: statusFilter === s ? "#fff" : NAVY,
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {error && (
        <p className="mb-4 rounded-lg px-3 py-2 text-sm" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.08)" }}>
          {(error as Error).message}
        </p>
      )}

      {isLoading ? <Loader2 className="animate-spin" /> : requests.length === 0 ? (
        <Card><p className="text-sm" style={{ color: NAVY, opacity: 0.55 }}>No {statusFilter} registration requests.</p></Card>
      ) : (
        <div className="space-y-3">
          {requests.map((req: any) => (
            <Card key={req.id}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-extrabold" style={{ color: NAVY }}>{req.name}</p>
                  <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: NAVY, opacity: 0.6 }}>
                    {req.contact_person_name && <span>Contact: {req.contact_person_name}</span>}
                    {req.industry && <span>Industry: {req.industry}</span>}
                    {req.abn && <span>ABN: {req.abn}</span>}
                    {req.phone && <span>Phone: {req.phone}</span>}
                    {req.email && <span>Email: {req.email}</span>}
                    {req.address && <span>Address: {req.address}</span>}
                  </div>
                  <div className="mt-2 text-[11px]" style={{ color: NAVY, opacity: 0.45 }}>
                    <span>Submitted by: {req.submittedByEmail || req.submitted_by_user_id}</span>
                    <span className="ml-3">{new Date(req.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</span>
                  </div>
                  {req.admin_note && (
                    <p className="mt-2 text-xs italic" style={{ color: NAVY, opacity: 0.5 }}>Admin note: {req.admin_note}</p>
                  )}
                </div>
                {statusFilter === "pending" && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openAction(req.id, "approve")}
                      className="rounded-lg px-3 py-1.5 text-xs font-bold text-white"
                      style={{ backgroundColor: ORANGE }}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => openAction(req.id, "reject")}
                      className="rounded-lg px-3 py-1.5 text-xs font-bold"
                      style={{ color: "#b91c1c", backgroundColor: "rgba(220,38,38,0.08)" }}
                    >
                      Reject
                    </button>
                  </div>
                )}
                {statusFilter !== "pending" && (
                  <span
                    className="text-xs font-bold rounded-full px-2.5 py-1 capitalize flex-shrink-0"
                    style={{
                      backgroundColor: req.status === "approved" ? `${ORANGE}18` : "rgba(220,38,38,0.08)",
                      color: req.status === "approved" ? ORANGE : "#b91c1c",
                    }}
                  >
                    {req.status}
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Approve / Reject modal */}
      {actionId && activeRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(23,33,43,0.55)" }}>
          <div className="w-full max-w-md rounded-lg bg-white p-5" style={{ border: `1px solid ${NAVY}18` }}>
            <p className="text-base font-extrabold mb-1" style={{ color: NAVY }}>
              {actionType === "approve" ? "Approve" : "Reject"} registration
            </p>
            <p className="text-sm mb-4" style={{ color: NAVY, opacity: 0.6 }}>
              <strong>{activeRequest.name}</strong>
            </p>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: NAVY }}>
              Admin note <span style={{ opacity: 0.45 }}>(optional — sent to requester)</span>
            </label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              rows={3}
              placeholder={actionType === "approve" ? "e.g. Welcome aboard! Your account is ready." : "e.g. Please resubmit with a valid ABN."}
              className="w-full rounded-lg px-3 py-2.5 text-sm outline-none resize-none"
              style={{ border: `1.5px solid ${NAVY}22`, color: NAVY, backgroundColor: "#f8f3ea" }}
            />
            {(approve.isError || reject.isError) && (
              <p className="mt-2 text-xs px-3 py-2 rounded-lg" style={{ color: "#991b1b", backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)" }}>
                {((approve.error || reject.error) as Error)?.message}
              </p>
            )}
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={closeAction} disabled={isPending} className="rounded-lg px-4 py-2 text-sm font-bold" style={{ backgroundColor: `${NAVY}0a`, color: NAVY }}>
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={isPending}
                className="rounded-lg px-4 py-2 text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: actionType === "approve" ? ORANGE : "#b91c1c" }}
              >
                {isPending && <Loader2 size={13} className="animate-spin" />}
                {actionType === "approve" ? "Approve" : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
}

export default AdminShell;
